import React, { useEffect, useRef, useState } from "react";
import { displayedToNaturalImageCoords } from "../../helper/imageOverlayCoordinates";
import { useNavigate } from "react-router-dom";
import {
  Stack, Typography, Box, Alert, Button, TextField, Chip, CircularProgress,
  LinearProgress, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Tooltip, Radio, Tabs, Tab, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import LaserMarkPanel from "../../components/LaserMarkPanel";
import MarkingTimeChip from "../../components/MarkingTimeChip";
import FactRow from "../../components/FactRow";
import PreviewToggle from "../../components/PreviewToggle";
import PaletteColorCard, { CardLabel } from "../../components/PaletteColorCard";
import PaintSwatch from "../../components/PaintSwatch";
import ReproductionPlacement from "../../components/ReproductionPlacement";
import {
  sidebarForType, reproductionStatusPresentation, reproductionListRoute, reproductionRoute,
  reproductionModeLabel, markingParameterChips, GRADIENT_DIRECTIONS, gradientScopeLabel, assignmentStep,
  suggestedCopyName, logoComponentsLabel, logoComponentCount,
} from "../../helper/reproductionFlow";
import { downloadBlob, getFilenameFromContentDisposition } from "../../helper/markingPreviewWindow";
import { downloadReproductionSource } from "../../helper/reproductionSource";
import { useReproductionParams } from "../../helper/entityRoutes";
import { selectionRoute } from "../../helper/selectionFlow";

const asField = (value) => (value === null || value === undefined ? "" : String(value));
const numberOrEmpty = (value) => (value === "" ? "" : Number(value));

// Final step: process the reproduction into a marking file. The
// reproduction is final here (the palette and the colors were fixed on
// the way in; there is no way back to the setup steps). Every run is its
// own history entry with its parameters and files, so the same
// reproduction can be marked at several sizes and places and every file
// stays downloadable. The run selected in the history is the one the
// visualization, the simulated result and the laser panel work with.
export default function ColorReproductionMarking() {
  const navigate = useNavigate();
  const { reproductionId, reproductionType: urlType } = useReproductionParams();

  const [details, setDetails] = useState(null);
  const [status, setStatus] = useState(null); // {value, label} of the newest run
  const [progressPercentage, setProgressPercentage] = useState(null);
  const [computationStage, setComputationStage] = useState(null);
  const [markings, setMarkings] = useState([]);
  const [selectedMarkingId, setSelectedMarkingId] = useState(null);
  const selectedMarkingIdRef = useRef(null);
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);

  // Logo reproductions: content height in the field and whether the SVG
  // canvas whitespace counts toward it. Both are per Generate.
  const [markingHeight, setMarkingHeight] = useState("");
  // Logo reproductions: the palette and the components, for the assigned
  // colors panel of the Reproduction tab.
  const [palette, setPalette] = useState([]);
  const [components, setComponents] = useState([]);
  // Image reproductions: size of the image on the plate, per Generate.
  const [imageWidthMm, setImageWidthMm] = useState("");
  const [imageHeightMm, setImageHeightMm] = useState("");
  // Gradient reproductions: size of the shape, per Generate.
  const [gradientWidthMm, setGradientWidthMm] = useState("");
  const [gradientHeightMm, setGradientHeightMm] = useState("");
  // Gradient and logo reproductions: where the marking lands in the field,
  // per Generate.
  const [positionMode, setPositionMode] = useState("center");
  const [positionXMm, setPositionXMm] = useState("");
  const [positionYMm, setPositionYMm] = useState("");
  // Where the substrate lies under the laser, per Generate: its top-left
  // corner in mm from the field's. Prefilled from the newest instance -
  // the plate does not move by itself between two of them.
  const [plateOffsetXMm, setPlateOffsetXMm] = useState(0);
  const [plateOffsetYMm, setPlateOffsetYMm] = useState(0);
  const [fieldSizeMm, setFieldSizeMm] = useState(null); // [width, height]
  const [gradientColorCount, setGradientColorCount] = useState(null);
  // The gradient's colors in blend order (palette entries), for the
  // Reproduction tab.
  const [gradientColors, setGradientColors] = useState([]);
  // The source paints with their own colour sequences (several when the
  // SVG has several fills); a paint without colours follows the first.
  const [gradientPaints, setGradientPaints] = useState([]);
  const [gradientDirection, setGradientDirection] = useState(null);
  const [gradientScope, setGradientScope] = useState(null);
  // Tabs: what was selected / process a run / the runs and their results.
  const [activeTab, setActiveTab] = useState("process");
  // Continue from a copy: the dialog asking for the copy's name.
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyName, setCopyName] = useState("");
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState(null);

  const [visualizationUrl, setVisualizationUrl] = useState("");
  const visualizationUrlRef = useRef("");
  const [sourceImageUrl, setSourceImageUrl] = useState("");
  const sourceImageUrlRef = useRef("");
  // Rendered preview of the colors on the source (gradient blend / palette-
  // mapped logo / gamut-mapped image), toggled against the source.
  const [renderedPreviewUrl, setRenderedPreviewUrl] = useState("");
  const renderedPreviewUrlRef = useRef("");
  const [renderedPreviewMissing, setRenderedPreviewMissing] = useState(null);
  const [reproductionView, setReproductionView] = useState("source");
  const [simulatedImageUrl, setSimulatedImageUrl] = useState("");
  const simulatedImageUrlRef = useRef("");

  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [skippedComponents, setSkippedComponents] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // Logo Setup tab: the component under the last click on the source image
  // or the rendered preview (its palette color's card is framed), or the
  // palette color whose card was clicked (all its components outlined),
  // and the outline over the image shown.
  const previewImageRef = useRef(null);
  const [clickedComponent, setClickedComponent] = useState(null);
  const [selectedSampleId, setSelectedSampleId] = useState(null);
  const [identifying, setIdentifying] = useState(false);
  const [componentMaskUrl, setComponentMaskUrl] = useState("");
  const componentMaskUrlRef = useRef("");
  const outlineRequestRef = useRef(0);
  const assignedCardRefs = useRef({});
  // Loading states: the instance list and every image panel show a spinner
  // until their request has answered (previews are rendered server-side and
  // can take a while), so an empty panel always means there is nothing.
  const [markingsState, setMarkingsState] = useState("loading"); // loading | ready | failed
  const [imagesLoading, setImagesLoading] = useState({
    source: true, rendered: true, visualization: false, simulated: false,
  });
  const setImageLoading = (key, value) => {
    if (mountedRef.current) setImagesLoading((previous) => ({ ...previous, [key]: value }));
  };

  const replaceObjectUrl = (ref, setter, blob) => {
    if (ref.current) URL.revokeObjectURL(ref.current);
    const url = blob ? URL.createObjectURL(blob) : "";
    ref.current = url;
    setter(url);
  };

  const fetchBlob = async (url, body) => {
    const response = await axios.post(url, body, { responseType: "blob" });
    return response.data;
  };

  // Images of one run: the marking visualization and (image / gradient)
  // the simulated halftone result. Missing files clear the panel.
  const fetchMarkingImages = async (markingId, withSimulated) => {
    if (!markingId) {
      replaceObjectUrl(visualizationUrlRef, setVisualizationUrl, null);
      replaceObjectUrl(simulatedImageUrlRef, setSimulatedImageUrl, null);
      return;
    }
    setImageLoading("visualization", true);
    if (withSimulated) setImageLoading("simulated", true);
    const visualization = (async () => {
      try {
        const blob = await fetchBlob("api/reproduction/marking/visualization", { marking_id: markingId });
        replaceObjectUrl(visualizationUrlRef, setVisualizationUrl, blob);
      } catch (error) {
        replaceObjectUrl(visualizationUrlRef, setVisualizationUrl, null);
      } finally {
        setImageLoading("visualization", false);
      }
    })();
    const simulated = withSimulated ? (async () => {
      try {
        const blob = await fetchBlob("api/reproduction/marking/simulated", { marking_id: markingId });
        replaceObjectUrl(simulatedImageUrlRef, setSimulatedImageUrl, blob);
      } catch (error) {
        replaceObjectUrl(simulatedImageUrlRef, setSimulatedImageUrl, null);
      } finally {
        setImageLoading("simulated", false);
      }
    })() : null;
    await Promise.all([visualization, simulated]);
  };

  // Uploaded source (logo in its original colors, image, or shape).
  const fetchSourceImage = async () => {
    try {
      const blob = await fetchBlob("api/reproduction/get_input_image_preview", { reproduction_id: reproductionId });
      replaceObjectUrl(sourceImageUrlRef, setSourceImageUrl, blob);
    } catch (error) {
      console.error("Error loading the source image:", error);
    } finally {
      setImageLoading("source", false);
    }
  };

  // The colors rendered on the source: the blended gradient, the palette-
  // mapped logo, or the gamut-mapped image (rendered for both gamut modes).
  const fetchRenderedPreview = async (type) => {
    let url;
    let missing;
    if (type === "GRADIENT_REPRODUCTION") {
      url = "api/reproduction/gradient/preview";
      missing = "No gradient colors selected.";
    } else if (type === "IMAGE_REPRODUCTION") {
      url = "api/reproduction/gamut/preview";
      missing = "No rendered preview before processing.";
    } else {
      url = "api/reproduction/get_reproduction_image_preview";
      missing = "No palette colors assigned.";
    }
    try {
      const blob = await fetchBlob(url, { reproduction_id: reproductionId });
      replaceObjectUrl(renderedPreviewUrlRef, setRenderedPreviewUrl, blob);
      setRenderedPreviewMissing(null);
    } catch (error) {
      replaceObjectUrl(renderedPreviewUrlRef, setRenderedPreviewUrl, null);
      setRenderedPreviewMissing(missing);
    } finally {
      setImageLoading("rendered", false);
    }
  };

  const selectMarking = async (markingId, withSimulated) => {
    selectedMarkingIdRef.current = markingId;
    setSelectedMarkingId(markingId);
    await fetchMarkingImages(markingId, withSimulated);
  };

  // The history of runs; keeps the current selection when it still exists,
  // otherwise shows the newest finished run.
  const loadMarkings = async (withSimulated) => {
    const response = await axios.post("api/reproduction/marking/list", {
      reproduction_id: reproductionId,
    });
    if (!mountedRef.current) return [];
    const rows = response.data.markings || [];
    setMarkings(rows);
    setMarkingsState("ready");
    const current = selectedMarkingIdRef.current;
    const stillThere = rows.find((row) => row.id === current && row.marking_file_available);
    const newestFinished = rows.find((row) => row.status === "FINISHED" && row.marking_file_available);
    const next = stillThere ? stillThere.id : (newestFinished ? newestFinished.id : null);
    if (next !== current || !next) {
      // Not awaited: the list shows at once, the image panels show their
      // own spinner until the selected instance's images arrive.
      selectMarking(next, withSimulated);
    }
    return rows;
  };

  // One status poll of the newest run: updates the progress bar. Returns
  // the raw payload for the polling loop.
  const refreshStatus = async () => {
    const statusResponse = await axios.post(
      "api/reproduction/get_reproduction_computation_status",
      { reproduction_id: reproductionId },
    );
    if (!mountedRef.current) return statusResponse.data;
    const payload = statusResponse.data;
    setStatus(payload.status || null);
    setProgressPercentage(payload.progress_percentage ?? null);
    setComputationStage(payload.stage || null);
    return payload;
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // Poll while the computation runs; land on the finished or failed state.
  const pollUntilDone = (withSimulated) => {
    stopPolling();
    const tick = async () => {
      if (!mountedRef.current) return;
      try {
        const payload = await refreshStatus();
        const value = payload?.status?.value;
        if (value === "COMPUTING") {
          pollTimerRef.current = setTimeout(tick, 2000);
          return;
        }
        if (value === "FINISHED") {
          setSuccessMessage("Marking file generated.");
          // Show the new run.
          selectedMarkingIdRef.current = payload.marking_id;
          setSelectedMarkingId(payload.marking_id);
          await loadMarkings(withSimulated);
          await fetchMarkingImages(payload.marking_id, withSimulated);
          setActiveTab("runs");
        } else {
          if (payload?.computation_error) setErrorMessage(payload.computation_error);
          await loadMarkings(withSimulated);
        }
      } catch (error) {
        console.error("Error polling the computation status:", error);
        pollTimerRef.current = setTimeout(tick, 5000);
      }
    };
    pollTimerRef.current = setTimeout(tick, 2000);
  };

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      try {
        const detailsResponse = await axios.post("api/reproduction/get_reproduction_details", {
          reproduction_id: reproductionId,
        });
        const loaded = detailsResponse.data;
        setDetails(loaded);
        const loadedType = loaded.reproduction_type || urlType;
        const isGradient = loadedType === "GRADIENT_REPRODUCTION";
        const withSimulated = loadedType === "IMAGE_REPRODUCTION" || isGradient;
        // Pre-fill the per-run inputs from the newest run, so Generate
        // repeats the last size and place unless changed.
        const latestParameters = loaded.latest_marking?.parameters || {};
        if (loaded.field_size_mm) setFieldSizeMm(loaded.field_size_mm);
        if (latestParameters.logo) {
          const logo = latestParameters.logo;
          setMarkingHeight(asField(logo.marking_height_mm));
          setPositionMode(logo.position_mode || "center");
          setPositionXMm(asField(logo.position_x_mm));
          setPositionYMm(asField(logo.position_y_mm));
          setPlateOffsetXMm(logo.plate_offset_x_mm || 0);
          setPlateOffsetYMm(logo.plate_offset_y_mm || 0);
        }
        if (loadedType === "IMAGE_REPRODUCTION") {
          const image = latestParameters.image || loaded.image || {};
          setImageWidthMm(asField(image.target_width_mm));
          setImageHeightMm(asField(image.target_height_mm));
          setPositionMode(image.position_mode || "center");
          setPositionXMm(asField(image.position_x_mm));
          setPositionYMm(asField(image.position_y_mm));
          setPlateOffsetXMm(image.plate_offset_x_mm || 0);
          setPlateOffsetYMm(image.plate_offset_y_mm || 0);
        }
        // The setup data, the server-rendered previews and the instances
        // load side by side: the instance list never waits for a preview.
        const loadSetupData = async () => {
          if (loadedType === "LOGO_REPRODUCTION") {
            try {
              const [paletteResponse, componentsResponse] = await Promise.all([
                axios.post("api/reproduction/get_palette", { reproduction_id: reproductionId }),
                axios.post("api/reproduction/get_components", { reproduction_id: reproductionId }),
              ]);
              setPalette(paletteResponse.data.palette || []);
              setComponents(componentsResponse.data.components || []);
            } catch (error) {
              console.error("Error loading the assigned colors:", error);
            }
          }
          if (isGradient) {
            const settingsResponse = await axios.post("api/reproduction/gradient/settings", {
              reproduction_id: reproductionId,
            });
            const settings = settingsResponse.data;
            const latest = settings.latest_marking_parameters || {};
            setGradientWidthMm(asField(latest.target_width_mm ?? settings.default_width_mm));
            setGradientHeightMm(asField(latest.target_height_mm));
            setPositionMode(latest.position_mode || "center");
            setPositionXMm(asField(latest.position_x_mm));
            setPositionYMm(asField(latest.position_y_mm));
            setPlateOffsetXMm(latest.plate_offset_x_mm || 0);
            setPlateOffsetYMm(latest.plate_offset_y_mm || 0);
            setFieldSizeMm(settings.field_size_mm || null);
            const paints = settings.setup?.paints || [];
            const firstIds = (settings.setup?.palette_sample_ids || []).map(String);
            // Every colour any marked paint blends through, in order of first use.
            const pickedIds = [];
            (paints.length ? paints : [{ palette_sample_ids: firstIds }]).forEach((paint) => {
              if (paint.excluded_from_marking) return;
              const ids = (paint.palette_sample_ids || []).length ? paint.palette_sample_ids : firstIds;
              ids.map(String).forEach((id) => { if (!pickedIds.includes(id)) pickedIds.push(id); });
            });
            setGradientColorCount(pickedIds.length || null);
            setGradientDirection(settings.setup?.direction || null);
            setGradientScope(settings.setup?.scope || null);
            try {
              const paletteResponse = await axios.post("api/reproduction/get_palette", {
                reproduction_id: reproductionId,
              });
              const bySample = new Map((paletteResponse.data.palette || []).map((e) => [e.sample_id, e]));
              const entryOf = (id) => bySample.get(id) || { sample_id: id, missing: true };
              setGradientColors(pickedIds.map(entryOf));
              setGradientPaints(paints.map((paint) => {
                const own = (paint.palette_sample_ids || []).map(String);
                return {
                  ...paint,
                  inherits: own.length === 0 && !paint.excluded_from_marking,
                  entries: (own.length ? own : firstIds).map(entryOf),
                };
              }));
            } catch (error) {
              console.error("Error loading the palette:", error);
            }
          }
        };
        const loadPreviews = () => Promise.all([fetchSourceImage(), fetchRenderedPreview(loadedType)]);
        const loadInstances = async () => {
          try {
            const rows = await loadMarkings(withSimulated);
            if (rows.some((row) => row.status === "FINISHED")) setActiveTab("runs");
          } catch (error) {
            console.error("Error loading the instances:", error);
            if (mountedRef.current) setMarkingsState("failed");
          }
          const payload = await refreshStatus();
          if (payload?.status?.value === "COMPUTING") {
            // A computation is already running (e.g. after a page reload):
            // resume the progress polling.
            pollUntilDone(withSimulated);
          } else if (payload?.status?.value === "FAILED" && payload?.computation_error) {
            setErrorMessage(payload.computation_error);
          }
        };
        await Promise.all([loadSetupData(), loadPreviews(), loadInstances()]);
      } catch (error) {
        console.error("Error loading the marking state:", error);
        setErrorMessage("Loading the marking state failed.");
      }
    };
    load();
    return () => {
      mountedRef.current = false;
      stopPolling();
      [visualizationUrlRef, sourceImageUrlRef, simulatedImageUrlRef, renderedPreviewUrlRef].forEach((ref) => {
        if (ref.current) URL.revokeObjectURL(ref.current);
      });
    };
  }, []);

  const reproductionType = details?.reproduction_type || urlType;
  const isGradientReproduction = reproductionType === "GRADIENT_REPRODUCTION";
  // Image and gradient reproductions share the halftone pipeline: no marking
  // height / whitespace settings here, source + simulated result panels.
  const isImageReproduction = reproductionType === "IMAGE_REPRODUCTION" || isGradientReproduction;
  const sourceLabel = isGradientReproduction
    ? "Source shape" : (isImageReproduction ? "Source image" : "Source logo");
  const reproductionViewOptions = [
    { value: "source", label: sourceLabel },
    { value: "rendered", label: "Rendered preview" },
  ];

  // The per-run parameters sent with Generate. The substrate offset goes
  // with every type: it decides what the laser can reach.
  const substrateParameters = () => ({
    plate_offset_x_mm: plateOffsetXMm,
    plate_offset_y_mm: plateOffsetYMm,
  });

  const runParameters = () => {
    if (isGradientReproduction) {
      return {
        target_width_mm: numberOrEmpty(gradientWidthMm),
        target_height_mm: numberOrEmpty(gradientHeightMm),
        position_mode: positionMode,
        position_x_mm: numberOrEmpty(positionXMm),
        position_y_mm: numberOrEmpty(positionYMm),
        ...substrateParameters(),
      };
    }
    if (isImageReproduction) {
      return {
        position_mode: positionMode,
        position_x_mm: numberOrEmpty(positionXMm),
        position_y_mm: numberOrEmpty(positionYMm),
        target_width_mm: numberOrEmpty(imageWidthMm),
        target_height_mm: numberOrEmpty(imageHeightMm),
        ...substrateParameters(),
      };
    }
    return {
      marking_height_mm: markingHeight === "" ? null : Number(markingHeight),
      position_mode: positionMode,
      position_x_mm: numberOrEmpty(positionXMm),
      position_y_mm: numberOrEmpty(positionYMm),
      ...substrateParameters(),
    };
  };

  const generateMarkingFile = async () => {
    setGenerating(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    setSkippedComponents([]);
    try {
      const response = await axios.post(
        "api/reproduction/start_reproduction_computation",
        { reproduction_id: reproductionId, ...runParameters() },
      );
      // The computation runs in the background; poll the status for the
      // progress bar until it lands on finished or failed.
      setStatus(response.data.status || { value: "COMPUTING", label: "Computing" });
      setProgressPercentage(0);
      setComputationStage("Starting");
      await loadMarkings(isImageReproduction);
      pollUntilDone(isImageReproduction);
    } catch (error) {
      console.error("Error generating the marking file:", error);
      const detail = error?.response?.data?.error;
      setErrorMessage(detail || "Processing the reproduction failed.");
      if (error?.response?.data?.marking_id) {
        await loadMarkings(isImageReproduction);
        await refreshStatus();
      }
    } finally {
      setGenerating(false);
    }
  };

  const downloadMarkingFile = async (markingId) => {
    setDownloadingId(markingId);
    setErrorMessage(null);
    try {
      const response = await axios.post(
        "api/reproduction/marking/file/download",
        { marking_id: markingId },
        { responseType: "blob" },
      );
      const filename = getFilenameFromContentDisposition(response.headers?.["content-disposition"])
        || `marking_file_${markingId}.json`;
      downloadBlob(response.data, filename);
    } catch (error) {
      console.error("Error downloading the marking file:", error);
      setErrorMessage("Downloading the marking file failed.");
    } finally {
      setDownloadingId(null);
    }
  };

  // The uploaded input file of the reproduction, under its original name.
  const [downloadingSource, setDownloadingSource] = useState(false);
  const downloadSourceFile = async () => {
    setDownloadingSource(true);
    setErrorMessage(null);
    try {
      await downloadReproductionSource(reproductionId, details?.original_file_name);
    } catch (error) {
      console.error("Error downloading the input file:", error);
      setErrorMessage("Downloading the input file failed.");
    } finally {
      setDownloadingSource(false);
    }
  };

  const getMarkingBlobForLaser = async () => {
    const response = await axios.post(
      "api/reproduction/marking/file/download",
      { marking_id: selectedMarkingId },
      { responseType: "blob" },
    );
    return {
      blob: response.data,
      filename: getFilenameFromContentDisposition(response.headers?.["content-disposition"])
        || `marking_file_${selectedMarkingId}.json`,
    };
  };

  // Removes one run (row + files). A run stuck in COMPUTING is cleared the
  // same way.
  const deleteMarking = async (markingId) => {
    setDeletingId(markingId);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      await axios.post("api/reproduction/marking/delete", { marking_id: markingId });
      stopPolling();
      await loadMarkings(isImageReproduction);
      await refreshStatus();
    } catch (error) {
      console.error("Error deleting the marking:", error);
      setErrorMessage(error?.response?.data?.error || "Deleting the marking failed.");
    } finally {
      setDeletingId(null);
    }
  };

  // A copy of this reproduction (source, setup, assignment; no instances)
  // that is open again: it starts on the assignment step.
  const copyTarget = assignmentStep(reproductionType);
  const openCopyDialog = () => {
    setCopyName(suggestedCopyName(details?.name));
    setCopyError(null);
    setCopyDialogOpen(true);
  };
  const duplicateReproduction = async () => {
    setCopying(true);
    setCopyError(null);
    try {
      const response = await axios.post("api/reproduction/duplicate", {
        reproduction_id: reproductionId, name: copyName.trim(),
      });
      const copy = response.data;
      setCopyDialogOpen(false);
      navigate(reproductionRoute(
        copy.reproduction_type || reproductionType, copy.reproduction_id, copyTarget.step,
      ));
    } catch (error) {
      console.error("Error copying the reproduction:", error);
      setCopyError(error?.response?.data?.error || "Copying the reproduction failed.");
      setCopying(false);
    }
  };

  const listRoute = reproductionListRoute(reproductionType);
  const finishFlow = () => navigate(listRoute);

  const presentation = reproductionStatusPresentation(status?.value);
  const computing = (status?.value || "").toUpperCase() === "COMPUTING";
  const componentCount = details ? logoComponentCount(details) : null;
  const selectedMarking = markings.find((row) => row.id === selectedMarkingId) || null;
  const finishedCount = markings.filter((row) => row.status === "FINISHED").length;
  const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");
  const directionLabel = (value) => (
    GRADIENT_DIRECTIONS.find((option) => option.value === value)?.label || value || "—"
  );

  // Logo reproductions: the palette entries in use, each with the
  // components (and their source colors) assigned to it, in palette order.
  const assignedColors = palette
    .map((entry) => {
      const members = components.filter((component) => component.palette_sample_id === entry.sample_id);
      if (members.length === 0) return null;
      const lines = members.filter((component) => component.component_type === "LINE").length;
      const layers = members.filter((component) => component.component_type === "LAYER").length;
      const shapes = members.length - lines - layers;
      const sourceHexes = [...new Set(members.map((component) => component.target_color_hex))];
      const parts = [];
      if (shapes) parts.push(`${shapes} shape${shapes === 1 ? "" : "s"}`);
      if (lines) parts.push(`${lines} line${lines === 1 ? "" : "s"}`);
      if (layers) parts.push(`${layers} color layer${layers === 1 ? "" : "s"}`);
      return { entry, members, sourceHexes, componentsLabel: parts.join(" · ") };
    })
    .filter(Boolean);
  const unassignedComponentCount = components.filter(
    (component) => !component.palette_sample_id && !component.excluded_from_marking,
  ).length;
  const excludedComponentCount = components.filter((component) => component.excluded_from_marking).length;

  // Opens the palette selection's overview on its gamut tab with this
  // colour preselected: the selection's kind and id go in the URL (its
  // type is looked up, the details carry only the id), the deep link in
  // the navigation state.
  const openInSelectionGamut = async (sampleId) => {
    const selectionId = details?.palette_selection_id ?? details?.palette_selection;
    if (!selectionId) return;
    let selectionType = null;
    try {
      const response = await axios.get(`api/color/selection/${selectionId}/`);
      selectionType = response.data?.selection_type || null;
    } catch (error) {
      console.error("Error loading the palette selection:", error);
    }
    navigate(
      selectionRoute(selectionType, selectionId, "overview"),
      { state: { tab: "gamut", sampleId: String(sampleId) } },
    );
  };

  // A click on the source image or the rendered preview of a logo (both
  // show the same canvas) names the component under it (the Edit colors
  // step's lookup): its palette color's card is framed in the Assigned
  // colors list and scrolled into view, the component outlined on the
  // image. A click on nothing clears both. The other direction: a click
  // on an assigned color's card frames it and outlines every component
  // with that color; a second click on the card clears it.
  // ``target`` is ``{component_id}`` or ``{sample_id}`` (the endpoint
  // outlines one component or all of a palette color), null for no
  // outline. Only the newest request's answer is shown.
  const showComponentOutline = async (target) => {
    const requestId = outlineRequestRef.current + 1;
    outlineRequestRef.current = requestId;
    let url = "";
    if (target) {
      try {
        const response = await axios.post(
          "api/reproduction/get_highlighted_component_mask",
          { reproduction_id: reproductionId, ...target },
          { responseType: "blob" },
        );
        url = URL.createObjectURL(response.data);
      } catch (error) {
        console.error("Error loading the component outline:", error);
      }
    }
    if (outlineRequestRef.current !== requestId) {
      if (url) URL.revokeObjectURL(url);
      return;
    }
    if (componentMaskUrlRef.current) URL.revokeObjectURL(componentMaskUrlRef.current);
    componentMaskUrlRef.current = url;
    setComponentMaskUrl(url);
  };

  const handleAssignedColorClick = (sampleId) => {
    setClickedComponent(null);
    if (sampleId === selectedSampleId) {
      setSelectedSampleId(null);
      showComponentOutline(null);
      return;
    }
    setSelectedSampleId(sampleId);
    showComponentOutline({ sample_id: sampleId });
  };

  const handlePreviewClick = async (event) => {
    const image = previewImageRef.current;
    if (!image || !image.naturalWidth || identifying) return;
    const rect = image.getBoundingClientRect();
    const { x: xNatural, y: yNatural } = displayedToNaturalImageCoords(
      image, event.clientX - rect.left, event.clientY - rect.top,
    );
    const x = Math.min(Math.max(xNatural / image.naturalWidth, 0), 1);
    const y = Math.min(Math.max(yNatural / image.naturalHeight, 0), 1);
    setIdentifying(true);
    try {
      const response = await axios.post("api/reproduction/identify_clicked_component", {
        reproduction_id: reproductionId, x, y,
      });
      const hit = response.data && response.data.component_id != null ? response.data : null;
      setSelectedSampleId(null);
      setClickedComponent(hit);
      await showComponentOutline(hit ? { component_id: hit.component_id } : null);
    } catch (error) {
      console.error("Error identifying the clicked component:", error);
      setErrorMessage("Identifying the clicked component failed.");
    } finally {
      setIdentifying(false);
    }
  };

  const highlightedSampleId = selectedSampleId || clickedComponent?.palette_sample_id || null;
  useEffect(() => {
    const card = highlightedSampleId ? assignedCardRefs.current[highlightedSampleId] : null;
    if (card && typeof card.scrollIntoView === "function") {
      card.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlightedSampleId]);
  useEffect(() => () => {
    if (componentMaskUrlRef.current) URL.revokeObjectURL(componentMaskUrlRef.current);
  }, []);

  // The caption under a clickable image: ``base`` plus the hint, the
  // palette color whose card was clicked, or what the last click hit.
  const clickedComponentCaption = (base) => {
    if (selectedSampleId) {
      const selected = assignedColors.find(({ entry }) => entry.sample_id === selectedSampleId);
      if (selected) return `Palette color ${selected.entry.index}: ${selected.componentsLabel} outlined.`;
    }
    if (!clickedComponent) {
      return `${base} Click a component to see its palette color, or a color to see its components.`;
    }
    const kind = clickedComponent.component_type === "LINE"
      ? "Line" : (clickedComponent.component_type === "LAYER" ? "Color layer" : "Shape");
    const source = String(clickedComponent.target_color_hex || "").toUpperCase();
    if (clickedComponent.excluded_from_marking) return `${kind} ${source}: not marked.`;
    const entry = palette.find((item) => item.sample_id === clickedComponent.palette_sample_id);
    if (!entry) return `${kind} ${source}: no palette color, skipped in the marking.`;
    return `${kind} ${source}: palette color ${entry.index}.`;
  };
  const clickable = isImageReproduction ? null : {
    onClick: handlePreviewClick, imageRef: previewImageRef, overlayUrl: componentMaskUrl,
  };

  const tabDefinitions = [
    { key: "reproduction", label: "Setup" },
    { key: "process", label: "Create Instance" },
    { key: "runs", label: "Instances" },
  ];

  // ``clickable``: the image answers clicks (``onClick``, reachable through
  // ``imageRef``) and shows ``overlayUrl`` (a transparent outline PNG of the
  // same canvas) on top.
  const imagePanel = (url, alt, placeholder, loading = false, clickable = null) => (
    <Box
      sx={{
        position: "relative", width: "100%", aspectRatio: "1",
        borderRadius: 2, overflow: "hidden", border: "1px solid",
        borderColor: "divider", bgcolor: "grey.50",
      }}
    >
      {url ? (
        <img
          ref={clickable ? clickable.imageRef : undefined}
          src={url}
          alt={alt}
          onClick={clickable ? clickable.onClick : undefined}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "contain", display: "block",
            cursor: clickable ? "crosshair" : undefined,
          }}
        />
      ) : null}
      {url && clickable && clickable.overlayUrl ? (
        <img
          src={clickable.overlayUrl}
          alt=""
          aria-hidden="true"
          data-testid="clicked-component-outline"
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "contain", display: "block", pointerEvents: "none",
          }}
        />
      ) : null}
      {!url && !loading ? (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, textAlign: "center" }}>
            {placeholder}
          </Typography>
        </Box>
      ) : null}
      {loading ? (
        <Box
          role="status"
          aria-label={`Loading ${alt}`}
          sx={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 1,
            bgcolor: url ? "rgba(255, 255, 255, 0.7)" : "transparent",
          }}
        >
          <CircularProgress size={28} />
          <Typography variant="caption" color="text.secondary">Loading…</Typography>
        </Box>
      ) : null}
    </Box>
  );

  // ---- Tab 1 (Setup): what the reproduction was set up with (read-only). ----
  const reproductionTab = (
    <Box
      sx={{
        display: "grid",
        gap: 3,
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
        alignItems: "start",
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Details
          </Typography>
          <Stack spacing={0}>
            <FactRow label="Palette selection" value={details?.palette_selection_name || "—"} />
            <FactRow label="Laser device" value={details?.laser_device_name || "—"} />
            <FactRow label="Substrate" value={details?.material_name || "—"} />
            <FactRow label={isGradientReproduction ? "Shape file" : (isImageReproduction ? "Image file" : "Logo file")} value={details?.original_file_name || "—"} />
            {isGradientReproduction && (
              <FactRow label="Direction" value={directionLabel(gradientDirection || details?.gradient?.direction)} />
            )}
            {isGradientReproduction && (
              <FactRow label="Blend spans" value={gradientScopeLabel(gradientScope || details?.gradient?.scope)} />
            )}
            {isGradientReproduction && gradientPaints.length <= 1 && (details?.gradient?.blend_centers || []).length > 0 && (
              <FactRow
                label="Blend centers"
                value={details.gradient.blend_centers.map((center) => `${Math.round(center * 100)} %`).join(", ")}
              />
            )}
            {!isImageReproduction && (
              <>
                <FactRow label="Mode" value={reproductionModeLabel(details?.logo?.reproduction_mode || details?.reproduction_mode)} />
                <FactRow label="Whitespace" value={details?.logo?.trim_content_whitespace ? "Pruned" : "Whole canvas"} />
                <FactRow label="Components" value={logoComponentsLabel(details)} />
                <FactRow label="Assigned" value={`${details?.assigned_count ?? 0} of ${componentCount ?? 0}`} />
              </>
            )}
            {isImageReproduction && !isGradientReproduction && (
              <>
                <FactRow label="Target size" value={[details?.image?.target_width_mm, details?.image?.target_height_mm].filter((v) => v !== null && v !== undefined).map((v) => `${v} mm`).join(" x ") || "default"} />
                <FactRow label="Iterations" value={String(details?.image?.iterations ?? "—")} />
                <FactRow label="Lightness mapping" value={details?.image?.lightness_mapping || "—"} />
                <FactRow label="Gamut mapping" value={details?.image?.gamut_mapping_mode || "—"} />
              </>
            )}
            <FactRow
              label="Instances"
              value={markingsState === "ready" ? `${markings.length} (${finishedCount} with a marking file)` : "—"}
            />
            {details?.copied_from_name ? (
              <FactRow label="Copied from" value={details.copied_from_name} />
            ) : null}
          </Stack>
          <Tooltip title={details?.original_file_name || ""}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={downloadingSource ? <CircularProgress size={14} /> : <DownloadIcon />}
                disabled={downloadingSource}
                onClick={downloadSourceFile}
                sx={{ textTransform: "none", fontWeight: 600, mt: 1.25 }}
              >
                Input file
              </Button>
            </span>
          </Tooltip>
        </Box>

        {isGradientReproduction && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Gradient colors
            </Typography>
            {gradientColors.length === 0 ? (
              <Typography variant="body2" color="text.disabled">No colors picked.</Typography>
            ) : gradientPaints.length > 1 ? (
              <Stack spacing={2}>
                {gradientPaints.map((paint) => (
                  <Box key={paint.id} data-testid={`gradient-paint-${paint.key}`}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                      <PaintSwatch display={paint.display} size={18} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{paint.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${paint.component_count} ${paint.component_count === 1 ? "shape" : "shapes"}`}
                        {paint.excluded_from_marking ? " \u2014 not marked" : (paint.inherits ? ` \u2014 follows ${gradientPaints[0].label}` : "")}
                        {!paint.excluded_from_marking && (paint.blend_centers || []).length > 0 && !paint.inherits
                          ? ` \u2014 blend centers ${paint.blend_centers.map((center) => `${Math.round(center * 100)} %`).join(", ")}`
                          : ""}
                      </Typography>
                    </Stack>
                    {paint.excluded_from_marking ? null : (
                      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5}>
                        {paint.entries.map((entry, position) => (
                          <PaletteColorCard
                            key={`${paint.id}-${entry.sample_id}`}
                            entry={entry}
                            testId={`gradient-paint-${paint.key}-color-${position}`}
                            facts={[
                              { label: "POSITION", value: String(position + 1) },
                              { label: "SELECTION ID", value: entry.missing ? "\u2014" : String(entry.index) },
                            ]}
                            onOpenInGamut={openInSelectionGamut}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5}>
                {gradientColors.map((entry, position) => (
                  <PaletteColorCard
                    key={entry.sample_id}
                    entry={entry}
                    testId={`gradient-color-${position}`}
                    facts={[
                      { label: "POSITION", value: String(position + 1) },
                      { label: "SELECTION ID", value: entry.missing ? "\u2014" : String(entry.index) },
                    ]}
                    onOpenInGamut={openInSelectionGamut}
                  />
                ))}
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Position: order in the blend. Selection ID: number of the entry in the palette selection.
            </Typography>
          </Box>
        )}

        {!isImageReproduction && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Assigned colors
            </Typography>
            {assignedColors.length === 0 ? (
              <Typography variant="body2" color="text.disabled">
                {palette.length === 0 ? "Loading the palette..." : "No component has a palette color."}
              </Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5}>
                {assignedColors.map(({ entry, sourceHexes, componentsLabel }) => (
                  <PaletteColorCard
                    key={entry.sample_id}
                    entry={entry}
                    testId={`assigned-color-${entry.index}`}
                    highlighted={entry.sample_id === highlightedSampleId}
                    cardRef={(node) => { assignedCardRefs.current[entry.sample_id] = node; }}
                    onClick={() => handleAssignedColorClick(entry.sample_id)}
                    ariaLabel={`Palette color ${entry.index}: show its components`}
                    facts={[
                      { label: "SELECTION ID", value: String(entry.index) },
                      { label: "COMPONENTS", value: componentsLabel },
                    ]}
                    onOpenInGamut={openInSelectionGamut}
                  >
                    <Box sx={{ mt: 0.75 }}>
                      <CardLabel>SOURCE COLORS</CardLabel>
                      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mt: 0.25 }}>
                        {sourceHexes.map((hex) => (
                          <Tooltip key={hex} title={`Source color ${String(hex).toUpperCase()}`}>
                            <Box
                              aria-label={`Source color ${String(hex).toUpperCase()}`}
                              sx={{
                                width: 18, height: 18, borderRadius: 0.75, bgcolor: hex,
                                border: "1px solid rgba(15, 23, 42, 0.2)",
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    </Box>
                  </PaletteColorCard>
                ))}
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {unassignedComponentCount > 0
                ? `${unassignedComponentCount} unassigned component${unassignedComponentCount === 1 ? "" : "s"} skipped. `
                : ""}
              {excludedComponentCount > 0
                ? `${excludedComponentCount} component${excludedComponentCount === 1 ? "" : "s"} not marked. `
                : ""}
              Selection ID: number of the entry in the palette selection. Source colors: logo colors
              replaced by the entry.
            </Typography>
          </Box>
        )}

        {/* A copy is the only way to change a final assignment. */}
        <Box
          sx={{
            p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Duplicate reproduction
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Editable copy with the current setup and assignment.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={openCopyDialog}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, flexShrink: 0 }}
            >
              Duplicate
            </Button>
          </Stack>
        </Box>
      </Stack>

      <Box>
        {reproductionView === "source"
          ? imagePanel(sourceImageUrl, sourceLabel,
            isGradientReproduction ? "The uploaded shape appears here."
              : (isImageReproduction ? "The uploaded image appears here." : "The uploaded logo appears here."),
            imagesLoading.source, clickable)
          : imagePanel(renderedPreviewUrl, "Rendered preview",
            renderedPreviewMissing || "The colors rendered on the source appear here.",
            imagesLoading.rendered, clickable)}
        <Stack alignItems="center" spacing={0.75} sx={{ mt: 1.5 }}>
          <PreviewToggle
            value={reproductionView}
            onChange={setReproductionView}
            options={reproductionViewOptions}
            ariaLabel="Reproduction view"
          />
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
            {reproductionView === "source"
              ? (isImageReproduction ? "Uploaded source." : clickedComponentCaption("Uploaded source."))
              : (isGradientReproduction
                ? "Blend of the selected colors."
                : (isImageReproduction
                  ? "Image mapped into the palette's gamut."
                  : clickedComponentCaption("Components in their assigned palette colors.")))}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );

  // The copy's name and what is carried over, before the copy is made.
  const carriedOver = [
    { key: "file", label: `${sourceLabel}: ${details?.original_file_name || "—"}` },
    { key: "palette", label: `Palette: ${details?.palette_selection_name || "—"}` },
    { key: "laser", label: `Laser: ${details?.laser_device_name || "—"}` },
    { key: "substrate", label: `Substrate: ${details?.material_name || "—"}` },
  ];
  if (isGradientReproduction) {
    carriedOver.push({ key: "colors", label: `Gradient colors: ${gradientColors.length}, ${directionLabel(gradientDirection || details?.gradient?.direction).toLowerCase()}` });
  } else if (isImageReproduction) {
    carriedOver.push({ key: "gamut", label: `Gamut mapping: ${details?.image?.gamut_mapping_mode || "—"}` });
    carriedOver.push({ key: "iterations", label: `Iterations: ${details?.image?.iterations ?? "—"}` });
  } else {
    carriedOver.push({ key: "mode", label: `Mode: ${reproductionModeLabel(details?.logo?.reproduction_mode || details?.reproduction_mode)}` });
    carriedOver.push({ key: "assigned", label: `Assigned colors: ${details?.assigned_count ?? 0} of ${componentCount ?? 0} components` });
  }
  const copyDialog = (
    <Dialog open={copyDialogOpen} onClose={() => { if (!copying) setCopyDialogOpen(false); }} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Duplicate reproduction</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {`The copy opens on the ${copyTarget.label} step. Instances are not included.`}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name of the copy"
            value={copyName}
            onChange={(event) => setCopyName(event.target.value)}
            inputProps={{ maxLength: 100 }}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, letterSpacing: "0.04em" }}>
              CARRIED OVER
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
              {carriedOver.map((chip) => (
                <Chip key={chip.key} size="small" variant="outlined" label={chip.label} />
              ))}
            </Stack>
          </Box>
          {copyError ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>{copyError}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setCopyDialogOpen(false)}
          disabled={copying}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={copying ? <CircularProgress size={16} thickness={5} color="inherit" /> : <ContentCopyIcon />}
          disabled={copying || !copyName.trim()}
          onClick={duplicateReproduction}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {copying ? "Copying..." : "Duplicate"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Where the instance lands on the plate (every reproduction type): the
  // same canvas the palette marking uses - the field, the plate and the
  // instance's box, dragged or typed.
  const positionBlock = (subject) => (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Position on the plate
      </Typography>
      <ReproductionPlacement
        reproductionId={reproductionId}
        params={runParameters()}
        positionMode={positionMode}
        position={positionXMm === "" || positionYMm === ""
          ? null : { x: Number(positionXMm), y: Number(positionYMm) }}
        onPositionChange={(next) => {
          setPositionXMm(next === null ? "" : String(next.x));
          setPositionYMm(next === null ? "" : String(next.y));
        }}
        onModeChange={setPositionMode}
        plateOffset={{ x: Number(plateOffsetXMm) || 0, y: Number(plateOffsetYMm) || 0 }}
        onPlateOffsetChange={(next) => {
          setPlateOffsetXMm(next.x);
          setPlateOffsetYMm(next.y);
        }}
        contentUrl={isImageReproduction ? sourceImageUrl : renderedPreviewUrl}
        subject={subject}
      />
    </Box>
  );

  // ---- Tab 2 (Create Instance): the per-instance parameters and the Process button. ----
  const processTab = (
    <Stack spacing={2.5}>
      {computing && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {`${computationStage || "Computing"}${progressPercentage != null ? ` — ${Math.round(progressPercentage)}%` : ""}`}
          </Typography>
          <LinearProgress
            variant={progressPercentage != null ? "determinate" : "indeterminate"}
            value={progressPercentage ?? 0}
            sx={{ maxWidth: 480, height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {!isImageReproduction && (
        <Stack spacing={2.5} sx={{ maxWidth: 720 }}>
          <Stack spacing={0.5}>
            <TextField
              label="Marking height (mm)"
              type="number"
              size="small"
              value={markingHeight}
              onChange={(event) => setMarkingHeight(event.target.value)}
              helperText={fieldSizeMm
                ? `Empty: fills the reachable substrate (${fieldSizeMm[0]} x ${fieldSizeMm[1]} mm). A height is used exactly.`
                : "Empty: fills the reachable substrate. A height is used exactly."}
              sx={{ maxWidth: 320 }}
            />
          </Stack>
          {positionBlock("logo")}
        </Stack>
      )}

      {isGradientReproduction && (
        <Stack spacing={2} sx={{ maxWidth: 720 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Shape size
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Target width (mm)"
                type="number"
                value={gradientWidthMm}
                onChange={(event) => setGradientWidthMm(event.target.value)}
                helperText="One size empty: aspect ratio kept. Both empty: 40 mm wide."
              />
              <TextField
                fullWidth
                size="small"
                label="Target height (mm)"
                type="number"
                value={gradientHeightMm}
                onChange={(event) => setGradientHeightMm(event.target.value)}
                helperText={fieldSizeMm
                  ? `The laser reaches ${fieldSizeMm[0]} x ${fieldSizeMm[1]} mm of the substrate.`
                  : "Size of the shape on the plate."}
              />
            </Stack>
          </Box>
          {positionBlock("shape")}
        </Stack>
      )}

      {isImageReproduction && !isGradientReproduction && (
        <Stack spacing={2} sx={{ maxWidth: 720 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Image size
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Target width (mm)"
                type="number"
                value={imageWidthMm}
                onChange={(event) => setImageWidthMm(event.target.value)}
                helperText="One size empty: aspect ratio kept. Both empty: 40 mm wide."
              />
              <TextField
                fullWidth
                size="small"
                label="Target height (mm)"
                type="number"
                value={imageHeightMm}
                onChange={(event) => setImageHeightMm(event.target.value)}
                helperText={fieldSizeMm
                  ? `The laser reaches ${fieldSizeMm[0]} x ${fieldSizeMm[1]} mm of the substrate.`
                  : "Size of the image on the plate."}
              />
            </Stack>
          </Box>
          {positionBlock("image")}
          <Typography variant="caption" color="text.secondary">
            The color separation can take several minutes.
          </Typography>
        </Stack>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          disableElevation
          startIcon={<PlayArrowIcon />}
          disabled={generating || computing}
          onClick={generateMarkingFile}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {generating || computing ? "Processing..." : "Process Reproduction"}
        </Button>
        {generating ? (
          <CircularProgress size={20} thickness={4} aria-label="Processing the reproduction" />
        ) : null}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Each instance is listed under Instances.
      </Typography>
    </Stack>
  );

  // A failed list load can be retried without reloading the page.
  const retryMarkings = async () => {
    setMarkingsState("loading");
    try {
      await loadMarkings(isImageReproduction);
    } catch (error) {
      console.error("Error loading the instances:", error);
      if (mountedRef.current) setMarkingsState("failed");
    }
  };
  const instancesLoading = markingsState === "loading";

  // ---- Tab 3 (Instances): the instances, the laser panel and the selected one's results. ----
  const runsTab = (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Marking history
        </Typography>
        {instancesLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              Loading instances…
            </Typography>
          </Stack>
        ) : markingsState === "failed" ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
            <Typography variant="body2" color="error">
              Loading the instances failed.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={retryMarkings}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Retry
            </Button>
          </Stack>
        ) : markings.length === 0 ? (
          <Typography variant="caption" color="text.disabled">
            No instances.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" aria-label="Marking history" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Processed</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Parameters</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {markings.map((row) => {
                  const rowPresentation = reproductionStatusPresentation(row.status);
                  const isSelected = row.id === selectedMarkingId;
                  const finished = row.status === "FINISHED" && row.marking_file_available;
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      selected={isSelected}
                      data-testid={`marking-row-${row.id}`}
                      onClick={() => { if (finished && !isSelected) selectMarking(row.id, isImageReproduction); }}
                      sx={{ cursor: finished ? "pointer" : "default" }}
                    >
                      <TableCell padding="checkbox">
                        <Radio
                          size="small"
                          checked={isSelected}
                          disabled={!finished}
                          inputProps={{ "aria-label": `Select marking ${row.id}` }}
                          onChange={() => selectMarking(row.id, isImageReproduction)}
                        />
                      </TableCell>
                      <TableCell>{formatDate(row.created_at)}</TableCell>
                      <TableCell>{row.created_by_email || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={rowPresentation.color}
                          label={rowPresentation.label}
                          sx={{ fontWeight: 600 }}
                        />
                        {row.status === "FAILED" && row.error ? (
                          <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                            {row.error}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                          {markingParameterChips(row, reproductionModeLabel).map((chip) => (
                            <Chip
                              key={chip.key}
                              size="small"
                              variant="outlined"
                              label={chip.label}
                              sx={{ cursor: "inherit" }}
                            />
                          ))}
                          <MarkingTimeChip value={row.predicted_marking_time} sx={{ cursor: "inherit" }} />
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Download marking file">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Download marking ${row.id}`}
                                disabled={!finished || downloadingId !== null}
                                onClick={(event) => { event.stopPropagation(); downloadMarkingFile(row.id); }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={row.status === "COMPUTING" ? "Discard the running computation" : "Delete this instance"}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={`Delete marking ${row.id}`}
                                disabled={deletingId !== null || generating}
                                onClick={(event) => { event.stopPropagation(); deleteMarking(row.id); }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      {selectedMarking && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            {`Selected instance: ${formatDate(selectedMarking.created_at)}`}
          </Typography>
          <LaserMarkPanel
            getMarkingBlob={getMarkingBlobForLaser}
            predictedMarkingTime={selectedMarking.predicted_marking_time}
            markingContext={{
              source: isGradientReproduction
                ? "gradient_reproduction"
                : (isImageReproduction ? "image_reproduction" : "logo_reproduction"),
              reproductionId,
              markingId: selectedMarkingId,
            }}
          />
        </Box>
      )}

      {/* Results of the selected instance: the simulated halftone (image /
          gradient) and the marking file visualization. */}
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          maxWidth: 900,
        }}
      >
        {isImageReproduction && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Simulated result
            </Typography>
            {imagePanel(simulatedImageUrl, "Simulated result", "No instance selected.",
              instancesLoading || imagesLoading.simulated)}
          </Box>
        )}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Marking file visualization
          </Typography>
          {imagePanel(visualizationUrl, "Marking file visualization", "No instance selected.",
            instancesLoading || imagesLoading.visualization)}
        </Box>
      </Box>
    </Stack>
  );

  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell
        title="Marking"
        subtitle="Instances of the final reproduction. Each instance is a marking file with its own parameters."
        stepSidebar={sidebarForType(
          reproductionType, 2,
          isGradientReproduction
            ? gradientColorCount
            : (isImageReproduction ? null : componentCount || null),
        )}
        headerBadges={
          <Chip size="small" color={presentation.color} label={presentation.label} sx={{ fontWeight: 600 }} />
        }
        footerBelowPaper={
          <Box sx={{ width: "100%" }}>
            <NavBarBottom
              variant="embedded"
              hideReturnButton
              nextLabel="Done"
              prevPage={listRoute}
              nextPage={listRoute}
              enableNextPage={false}
              nextPageClickFunc={finishFlow}
            />
          </Box>
        }
      >
        <Stack spacing={2.5} sx={{ width: "100%" }}>
          {errorMessage && (
            <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}
          {skippedComponents.length > 0 && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {`${skippedComponents.length} component${skippedComponents.length === 1 ? "" : "s"} were skipped:`}
              </Typography>
              {skippedComponents.map((skipped) => (
                <Typography key={skipped.component_index} variant="body2">
                  {`Component ${skipped.component_index}: ${skipped.reason}`}
                </Typography>
              ))}
            </Alert>
          )}

          <Tabs
            value={tabDefinitions.findIndex((tab) => tab.key === activeTab)}
            onChange={(_event, value) => setActiveTab(tabDefinitions[value].key)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9rem" },
            }}
            aria-label="Marking"
          >
            {tabDefinitions.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>

          {activeTab === "reproduction" && reproductionTab}
          {activeTab === "process" && processTab}
          {activeTab === "runs" && runsTab}
        </Stack>
      </ExplorationPageShell>
      {copyDialog}
    </Stack>
  );
}
